import type { Metric } from '@atlassian/browser-metrics/metric';

import { viewDeadAirMetrics } from '../../common/utils/browser-metrics/viewDeadAirMetrics';
import type {
	ViewDeadAirBroker,
	ViewDeadAirContentType,
	ViewDeadAirFinishReason,
	ViewDeadAirMark,
	ViewDeadAirResult,
	ViewDeadAirSessionConfig,
} from '../../ui/view-dead-air-tracker/types';
import { calcExcessDeadAirAboveThreshold } from './calcExcessDeadAirAboveThreshold';
import { shouldExcludeDeadAirMetrics } from './dead-air-metrics-filter';
import { shouldExcludeInterval } from './shouldExcludeInterval';

export type ViewDeadAirBrokerConfig = {
	onEmit?: (result: ViewDeadAirResult) => void;
};

type BrokerState = {
	isActive: boolean;
	sessionConfig: ViewDeadAirSessionConfig | null;
	startTimestamp: number;
	previousMark: ViewDeadAirMark | null;
	intervals: number[];
	maxDeadAirMs: number;
	finishedOnce: boolean;
	identity: string | null;
};

const createInitialState = (): BrokerState => ({
	isActive: false,
	sessionConfig: null,
	startTimestamp: 0,
	previousMark: null,
	intervals: [],
	maxDeadAirMs: 0,
	finishedOnce: false,
	identity: null,
});

const LOG_PREFIX = '[ViewDeadAir]';
const DEBUG_LOGGING = false; // Set to true for debugging

// Threshold constants in milliseconds
const THRESHOLD_3_SECONDS = 3000;
const THRESHOLD_5_SECONDS = 5000;
const THRESHOLD_7_SECONDS = 7000;

export function createViewDeadAirBroker(brokerConfig?: ViewDeadAirBrokerConfig): ViewDeadAirBroker {
	let state = createInitialState();

	function start(config: ViewDeadAirSessionConfig): void {
		// If a session was active and never finished, finish it first to avoid losing data
		if (state.isActive && !state.finishedOnce) {
			if (DEBUG_LOGGING) {
				// eslint-disable-next-line no-console
				console.log(`${LOG_PREFIX} interrupting active session to start new one`);
			}
			finish('interrupted');
		}

		// Preserve identity across session resets
		const currentIdentity = state.identity;
		state = createInitialState();
		state.identity = currentIdentity;
		state.isActive = true;
		state.sessionConfig = config;
		state.startTimestamp = performance.now();

		if (DEBUG_LOGGING) {
			// eslint-disable-next-line no-console
			console.log(`${LOG_PREFIX} Session started:`, config);
		}
	}

	function mark({ id, contentType }: { id: string; contentType: ViewDeadAirContentType }): void {
		if (!state.isActive) {
			return;
		}

		const now = performance.now();
		const currentMark: ViewDeadAirMark = { id, contentType, timestamp: now };

		if (!state.previousMark) {
			// First mark — interval from session start
			const interval = now - state.startTimestamp;

			if (shouldExcludeInterval(interval)) {
				state.previousMark = currentMark;
				return;
			}

			state.intervals.push(interval);
			state.maxDeadAirMs = interval;
			state.previousMark = currentMark;

			if (DEBUG_LOGGING) {
				// eslint-disable-next-line no-console
				console.log(
					`${LOG_PREFIX} interval: ${interval.toFixed(
						1,
					)}ms Mark: id=${id}, contentType=${contentType}`,
				);
			}
			return;
		}

		// Aggregate consecutive stream marks with same id and same contentType
		if (
			contentType === 'stream' &&
			state.previousMark.id === id &&
			state.previousMark.contentType === 'stream'
		) {
			state.previousMark.timestamp = now;
			return;
		}

		const interval = now - state.previousMark.timestamp;

		if (shouldExcludeInterval(interval)) {
			state.previousMark = currentMark;
			return;
		}

		state.intervals.push(interval);
		if (interval > state.maxDeadAirMs) {
			state.maxDeadAirMs = interval;
		}
		state.previousMark = currentMark;

		if (DEBUG_LOGGING) {
			// eslint-disable-next-line no-console
			console.log(
				`${LOG_PREFIX} interval: ${interval.toFixed(
					1,
				)}ms Mark: id=${id}, contentType=${contentType}`,
			);
		}
	}

	function finish(reason: ViewDeadAirFinishReason): void {
		if (state.finishedOnce || !state.isActive) {
			return;
		}

		state.finishedOnce = true;
		state.isActive = false;

		const { intervals, maxDeadAirMs, sessionConfig } = state;

		// Calculate excess dead air time above thresholds
		const excessDeadAirAbove3s = calcExcessDeadAirAboveThreshold(intervals, THRESHOLD_3_SECONDS);
		const excessDeadAirAbove5s = calcExcessDeadAirAboveThreshold(intervals, THRESHOLD_5_SECONDS);
		const excessDeadAirAbove7s = calcExcessDeadAirAboveThreshold(intervals, THRESHOLD_7_SECONDS);

		const result: ViewDeadAirResult = {
			sessionConfig: sessionConfig!,
			reason,
			intervals,
			maxDeadAirMs,
		};

		brokerConfig?.onEmit?.(result);

		const shouldSkip = shouldExcludeDeadAirMetrics({
			isAIFC: sessionConfig?.isAIFCEnabled ?? false,
			isDeepResearch: sessionConfig?.reasoningMode === 'deep-research',
		});

		if (shouldSkip) {
			if (DEBUG_LOGGING) {
				// eslint-disable-next-line no-console
				console.log(
					`${LOG_PREFIX} Skipping metrics (AIFC=${sessionConfig?.isAIFCEnabled}, reasoningMode=${sessionConfig?.reasoningMode})`,
				);
			}
			return;
		}

		// Send browser metric - truncate all numeric values to integers before egress
		const metric: Metric = viewDeadAirMetrics.createViewDeadAirMetrics();
		const now = performance.now();
		const intervalsInt = intervals.map((i) => Math.trunc(i));

		metric.start({ startTime: now - maxDeadAirMs });
		metric.stop({
			stopTime: now,
			customData: {
				// Session config
				messageId: sessionConfig?.messageId ?? '',
				conversationId: sessionConfig?.conversationId ?? '',
				traceId: sessionConfig?.traceId ?? '',
				productKey: sessionConfig?.productKey ?? '',
				reasoningMode: sessionConfig?.reasoningMode ?? '',
				isAIFCEnabled: sessionConfig?.isAIFCEnabled ?? false,
				isWebSearchEnabled: sessionConfig?.isWebSearchEnabled ?? false,
				// Result data
				reason,
				maxDeadAirMs: Math.trunc(maxDeadAirMs),
				intervalCount: intervalsInt.length,
				intervals: JSON.stringify(intervalsInt),
				// Excess dead air time above thresholds
				excessDeadAirAbove3s: Math.trunc(excessDeadAirAbove3s),
				excessDeadAirAbove5s: Math.trunc(excessDeadAirAbove5s),
				excessDeadAirAbove7s: Math.trunc(excessDeadAirAbove7s),
			},
		});

		if (DEBUG_LOGGING) {
			// eslint-disable-next-line no-console
			console.log(`${LOG_PREFIX} Session finished (${reason}):`, {
				intervals: result.intervals,
				maxDeadAirMs: result.maxDeadAirMs,
				excessDeadAirAbove3s,
				excessDeadAirAbove5s,
				excessDeadAirAbove7s,
			});
		}
	}

	function updateIdentity(newIdentity: string): void {
		// If session is active and identity changed, finish the current session
		if (state.isActive && state.identity !== null && state.identity !== newIdentity) {
			if (DEBUG_LOGGING) {
				// eslint-disable-next-line no-console
				console.log(
					`${LOG_PREFIX} identity changed from ${state.identity} to ${newIdentity}, finishing session`,
				);
			}
			finish('identity-changed');
		}
		state.identity = newIdentity;
	}

	return { start, mark, finish, updateIdentity };
}
